import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import React, { useState, useCallback } from "react";
import { Modal } from "@mui/material";
import PageLoader from "../../../../common/ui/PageLoader";
import SegmentedStepper from "../common/SegmentedStepper";
import LogicBuilderStep1 from "./LogicBuilderStep1";
import LogicBuilderStep2 from "./LogicBuilderStep2";
import { useSelector } from "react-redux";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import LogicBuilderStep3 from "./LogicBuilderStep3";
import { updateLogic } from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import { enReportServiceType } from "../seviceBuilder/helper";
import { getProviderId } from "../../store/helper";
import { DEFAULT_PROVIDER } from "../labBuilder/SavedPanelsScreen";
import { OperatorType } from "../../utils/ruleFormatters";

// Re-export from utility file for backward compatibility
export {
  CATEGORY_SERVER_MAPPING,
  getServerCategoryKey,
  getDisplayCategoryName,
  OperatorType,
  OPERATOR_DISPLAY_MAPPING,
  getOperatorDisplayText,
  getOperatorFromDisplayText,
} from "../../utils/ruleFormatters";

export interface Field {
  name: string;
  operator: OperatorType | null;
  value: string | number | string[] | number[] | null;
}

export interface Condition {
  object_name: string | null;
  fields: Field[];
  match: "all" | "any";
}

export interface Rule {
  id: string;
  conditions: Condition[];
  match: "all" | "any";
}

export interface LogicRules {
  list: Rule[];
  match: "all" | "any";
}

export interface ServiceRow {
  id: string;
  selectedType: string | null;
  selectedServiceId: string | null;
  isLabPanel?: boolean;
  // The full panel object chosen via the Select Lab Panel modal. Carried on the
  // row so custom AND provider (e.g. Quest default) panels resolve downstream
  // without a separate per-source lookup.
  labPanelData?: any;
}

export interface ServiceData {
  id: string;
  data: any;
  loading: boolean;
  error: string | null;
}

export interface FinalLogicData {
  name: string;
  rules: LogicRules;
  services: any[]; // Array of service objects from API response
  labPanels: any[]; // Array of lab panel config objects
}

type Props = {
  openModal: boolean;
  handleClose: () => void;
  onLogicSaved?: () => void | Promise<void>;
  editingLogic?: any | null;
  isEditMode?: boolean;
};

const NewLogicModal = ({
  openModal,
  handleClose,
  onLogicSaved,
  editingLogic = null,
  isEditMode = false,
}: Props) => {
  const theme: Theme = useTheme();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();
  const categoryOptions = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.categoryOptions
  );
  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  // Provider (e.g. Quest default) panel catalogs, keyed by provider — used to
  // resolve a selected panel's provider when it isn't one of the user's own.
  const providerLabPanelsByProvider = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.providerLabPanels
  );
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [serviceRows, setServiceRows] = useState<ServiceRow[]>([
    {
      id: Date.now().toString(),
      selectedType: null,
      selectedServiceId: null,
    },
  ]);

  // State for building the final logic data to send to backend
  const [finalLogicData, setFinalLogicData] = useState<FinalLogicData>(() => {
    // Initialize with one rule template
    const firstField: Field = {
      name: "",
      operator: null,
      value: null,
    };

    const firstCondition: Condition = {
      object_name: null,
      fields: [firstField],
      match: "all",
    };

    const firstRule: Rule = {
      id: Date.now().toString(),
      conditions: [firstCondition],
      match: "all",
    };

    return {
      name: "",
      rules: {
        list: [firstRule],
        match: "all",
      },
      services: [],
      labPanels: [],
    };
  });

  // Helper to update services in finalLogicData
  const updateFinalServices = useCallback((servicesArray: any[]) => {
    setFinalLogicData((prev) => ({
      ...prev,
      services: servicesArray, // Store the API response array directly
    }));
  }, []);

  // Helper to update lab panels in finalLogicData
  const updateFinalLabPanels = useCallback((labPanelsArray: any[]) => {
    setFinalLogicData((prev) => ({
      ...prev,
      labPanels: labPanelsArray,
    }));
  }, []);

  // Load editing logic data when modal opens in edit mode
  React.useEffect(() => {
    if (openModal && isEditMode && editingLogic) {
      // Set the name and rules
      setFinalLogicData({
        name: editingLogic.name || "",
        rules: editingLogic.rules || {
          list: [
            {
              id: Date.now().toString(),
              conditions: [
                {
                  object_name: null,
                  fields: [{ name: "", operator: null, value: null }],
                  match: "all",
                },
              ],
              match: "all",
            },
          ],
          match: "all",
        },
        services: editingLogic.services || [],
        labPanels: (editingLogic.labPanels || []).map((lp: any) => ({
          userLabPanelID: lp.userLabPanelID || lp.id,
          panelData: lp,
          startOffset: lp.startOffset ?? 0,
          startOffsetUnit: lp.startOffsetUnit ?? 4,
        })),
      });

      // Build service rows from services
      const serviceRowsList = (editingLogic.services || []).map(
        (service: any, index: number) => {
          const serviceTypeDisplayName = service.serviceType
            ? enReportServiceType[service.serviceType]
            : null;
          const actualServiceId = service.userServiceID || service.id;

          return {
            id: `${service.id || Date.now() + index}`,
            selectedType: serviceTypeDisplayName,
            selectedServiceId: actualServiceId?.toString() || null,
          };
        }
      );

      // Build lab panel rows from labPanels. The saved payload only carries the
      // panel id, so resolve the full panel (name, provider, etc.) from the
      // loaded custom + provider catalogs for display.
      const knownPanels = [
        ...savedLabPanels,
        ...Object.values(providerLabPanelsByProvider ?? {}).flat(),
      ];
      const labPanelRowsList = (editingLogic.labPanels || []).map(
        (lp: any, index: number) => {
          const panelId = lp.userLabPanelID || lp.id;
          // The loaded logic's labPanels item is already enriched with the panel
          // name/provider/items by the backend; prefer a fresh catalog match, but
          // fall back to the item itself so the Name always shows.
          const panel =
            knownPanels.find(
              (p: any) => p?.id?.toString() === panelId?.toString()
            ) ?? lp;
          return {
            id: `lp-${panelId || Date.now() + index}`,
            selectedType: enReportServiceType[8],
            selectedServiceId: panelId?.toString() || null,
            isLabPanel: true,
            labPanelData: panel,
          };
        }
      );

      // Merge service rows and lab panel rows
      const allRows = [...serviceRowsList, ...labPanelRowsList];
      if (allRows.length > 0) {
        setServiceRows(allRows);
      }
    }
  }, [openModal, isEditMode, editingLogic]);

  const handleCloseFunction = () => {
    handleClose();
    setIsSaving(false);
    setCurrentStep(1);
    setValidationErrors([]);
    setServiceRows([
      {
        id: Date.now().toString(),
        selectedType: null,
        selectedServiceId: null,
      },
    ]);

    // Reset finalLogicData to initial state
    const firstField: Field = {
      name: "",
      operator: null,
      value: null,
    };

    const firstCondition: Condition = {
      object_name: null,
      fields: [firstField],
      match: "all",
    };

    const firstRule: Rule = {
      id: Date.now().toString(),
      conditions: [firstCondition],
      match: "all",
    };

    setFinalLogicData({
      name: "",
      rules: {
        list: [firstRule],
        match: "all",
      },
      services: [],
      labPanels: [],
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Prepare services - ensure all have userLogicBuilderID when editing
      let servicesToSend = finalLogicData.services;
      if (isEditMode && editingLogic?.id) {
        servicesToSend = finalLogicData.services.map((service: any) => ({
          ...service,
          // Set userLogicBuilderID to current logic ID if it's null (newly added services)
          userLogicBuilderID: service.userLogicBuilderID || editingLogic.id,
        }));
      }

      // Look up a panel's provider by id across the user's own panels and every
      // cached provider catalog, so we can send the numeric lab id (enLabID).
      const allKnownPanels = [
        ...savedLabPanels,
        ...Object.values(providerLabPanelsByProvider ?? {}).flat(),
      ];
      const resolveProviderId = (panelId: string): number | null => {
        const panel = allKnownPanels.find(
          (p: any) => p?.id?.toString() === panelId?.toString()
        );
        // Fall back to the default provider (Quest) when the panel can't be
        // resolved — the only provider currently offered.
        return (
          getProviderId(panel?.provider) ?? getProviderId(DEFAULT_PROVIDER)
        );
      };

      // Prepare lab panels for save - map to API format
      const labPanelsToSend = finalLogicData.labPanels.map((lp: any) => ({
        userLabPanelID: lp.userLabPanelID,
        provider: resolveProviderId(lp.userLabPanelID),
        startOffset: lp.startOffset ?? 0,
        startOffsetUnit: lp.startOffsetUnit ?? 4,
      }));

      // Prepare data to send to server
      const dataToSend: any = {
        name: finalLogicData.name,
        rules: finalLogicData.rules,
        services: servicesToSend,
        labPanels: labPanelsToSend,
      };

      // Include ID when editing to update existing logic
      if (isEditMode && editingLogic?.id) {
        dataToSend.id = editingLogic.id;
      }

      const response = await updateLogic(dataToSend);

      // Close modal after successful save
      handleCloseFunction();

      // Call the onLogicSaved callback to refetch data
      if (onLogicSaved) {
        await onLogicSaved();
      }

      // Show success snackbar
      ASSetSnackBarMessage({
        title: "Success",
        message: "Logic saved successfully!",
      });
      ASSetTypeSnackBar("success");
      ASSetOpenSnackBar(true);
    } catch (error) {
      console.error("Error saving logic:", error);

      // Show error snackbar
      ASSetSnackBarMessage({
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to save logic. Please try again.",
      });
      ASSetTypeSnackBar("alert");
      ASSetOpenSnackBar(true);

      setIsSaving(false);
    }
  };

  const handleStepValidation = (step: number): boolean => {
    setValidationErrors([]); // Clear previous errors

    if (step === 1) {
      const { validateStep1 } = require("./LogicBuilderStep1");
      const validation = validateStep1(
        finalLogicData.name,
        finalLogicData.rules.list[0],
        categoryOptions
      );
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    if (step === 2) {
      const { validateStep2 } = require("./LogicBuilderStep2");
      const validation = validateStep2(serviceRows);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    if (step === 3) {
      const { validateStep3 } = require("./LogicBuilderStep3");
      const validation = validateStep3(
        finalLogicData.services,
        "logicBuilder",
        undefined,
        finalLogicData.labPanels
      );
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    return true;
  };

  const handleLogicNameChange = useCallback((name: string) => {
    setFinalLogicData((prev) => ({
      ...prev,
      name: name,
    }));
  }, []);

  const updateConditionObjectName = useCallback(
    (conditionIndex: number, objectName: string | null) => {
      setFinalLogicData((prev) => {
        const updatedRules = {
          ...prev.rules,
          list: [
            {
              ...prev.rules.list[0],
              conditions: prev.rules.list[0].conditions.map((condition, cIdx) =>
                cIdx === conditionIndex
                  ? {
                      ...condition,
                      object_name: objectName,
                    }
                  : condition
              ),
            },
          ],
        };
        return {
          ...prev,
          rules: updatedRules,
        };
      });
    },
    []
  );

  const updateFieldProperty = useCallback(
    (
      conditionIndex: number,
      fieldIndex: number,
      property: keyof Field,
      value: any
    ) => {
      setFinalLogicData((prev) => {
        const updatedRules = {
          ...prev.rules,
          list: [
            {
              ...prev.rules.list[0],
              conditions: prev.rules.list[0].conditions.map((condition, cIdx) =>
                cIdx === conditionIndex
                  ? {
                      ...condition,
                      fields: condition.fields.map((field, fIdx) =>
                        fIdx === fieldIndex
                          ? { ...field, [property]: value }
                          : field
                      ),
                    }
                  : condition
              ),
            },
          ],
        };
        return {
          ...prev,
          rules: updatedRules,
        };
      });
    },
    []
  );

  const addCondition = useCallback((ruleIndex: number) => {
    setFinalLogicData((prev) => {
      const newField: Field = {
        name: "",
        operator: null,
        value: null,
      };

      const newCondition: Condition = {
        object_name: null,
        fields: [newField],
        match: "all",
      };

      const updatedRules = {
        ...prev.rules,
        list: prev.rules.list.map((rule, rIdx) =>
          rIdx === ruleIndex
            ? {
                ...rule,
                conditions: [...rule.conditions, newCondition],
              }
            : rule
        ),
      };
      return {
        ...prev,
        rules: updatedRules,
      };
    });
  }, []);

  const updateConditionMatch = useCallback((match: "all" | "any") => {
    setFinalLogicData((prev) => {
      const updatedRules = {
        ...prev.rules,
        list: [
          {
            ...prev.rules.list[0],
            match,
          },
        ],
      };
      return {
        ...prev,
        rules: updatedRules,
      };
    });
  }, []);

  const updateParameterMatch = useCallback(
    (conditionIndex: number, match: "all" | "any") => {
      setFinalLogicData((prev) => {
        const updatedRules = {
          ...prev.rules,
          list: [
            {
              ...prev.rules.list[0],
              conditions: prev.rules.list[0].conditions.map((condition, cIdx) =>
                cIdx === conditionIndex
                  ? {
                      ...condition,
                      match,
                    }
                  : condition
              ),
            },
          ],
        };
        return {
          ...prev,
          rules: updatedRules,
        };
      });
    },
    []
  );

  const addParameter = useCallback((conditionIndex: number) => {
    setFinalLogicData((prev) => {
      const newField: Field = {
        name: "",
        operator: null,
        value: null,
      };

      const updatedRules = {
        ...prev.rules,
        list: [
          {
            ...prev.rules.list[0],
            conditions: prev.rules.list[0].conditions.map((condition, cIdx) =>
              cIdx === conditionIndex
                ? {
                    ...condition,
                    fields: [...condition.fields, newField],
                  }
                : condition
            ),
          },
        ],
      };
      return {
        ...prev,
        rules: updatedRules,
      };
    });
  }, []);

  const deleteParameter = useCallback(
    (conditionIndex: number, fieldIndex: number) => {
      setFinalLogicData((prev) => {
        const updatedRules = {
          ...prev.rules,
          list: [
            {
              ...prev.rules.list[0],
              conditions: prev.rules.list[0].conditions.map((condition, cIdx) =>
                cIdx === conditionIndex
                  ? {
                      ...condition,
                      fields: condition.fields.filter(
                        (_, fIdx) => fIdx !== fieldIndex
                      ),
                      match:
                        condition.fields.length === 2 ? "all" : condition.match,
                    }
                  : condition
              ),
            },
          ],
        };
        return {
          ...prev,
          rules: updatedRules,
        };
      });
    },
    []
  );

  const deleteCondition = useCallback(
    (ruleIndex: number, conditionIndex: number) => {
      setFinalLogicData((prev) => {
        const targetRule = prev.rules.list[ruleIndex];

        let updatedRules: LogicRules;

        if (targetRule.conditions.length === 1) {
          const newList = prev.rules.list.filter(
            (_, rIdx) => rIdx !== ruleIndex
          );
          updatedRules = {
            ...prev.rules,
            list: newList,
            match: newList.length <= 1 ? "all" : prev.rules.match,
          };
        } else {
          updatedRules = {
            ...prev.rules,
            list: prev.rules.list.map((rule, rIdx) =>
              rIdx === ruleIndex
                ? {
                    ...rule,
                    conditions: rule.conditions.filter(
                      (_, cIdx) => cIdx !== conditionIndex
                    ),
                    match: rule.conditions.length === 2 ? "all" : rule.match,
                  }
                : rule
            ),
          };
        }

        return {
          ...prev,
          rules: updatedRules,
        };
      });
    },
    []
  );

  return (
    <Modal open={openModal} onClose={handleCloseFunction} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        <div style={styles.modalHeaderContainer}>
          <div style={styles.modalHeaderTitle(theme)}>
            {isEditMode ? "Edit Logic" : "New Logic"}
          </div>
          <div onClick={handleCloseFunction} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>
        {isSaving ? (
          <PageLoader />
        ) : (
          <SegmentedStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isNextButtonDisabled={false}
            handleSave={handleSave}
            isSaveDisabled={isSaving}
            startStep={1}
            totalSteps={3}
            isEditMode={isEditMode}
            onStepValidation={handleStepValidation}
            onStepChange={(newStep, direction) => {
              // Only clear errors when going back, not forward
              if (direction === "back") {
                setValidationErrors([]);
              }
              setCurrentStep(newStep);
            }}
            validationErrors={validationErrors}
          >
            {currentStep === 1 && (
              <LogicBuilderStep1
                name={finalLogicData.name}
                setLogicName={handleLogicNameChange}
                rules={finalLogicData.rules.list[0]}
                addCondition={addCondition}
                addParameter={addParameter}
                deleteCondition={deleteCondition}
                deleteParameter={deleteParameter}
                updateConditionMatch={updateConditionMatch}
                updateParameterMatch={updateParameterMatch}
                updateConditionObjectName={updateConditionObjectName}
                updateFieldProperty={updateFieldProperty}
              />
            )}
            {currentStep === 2 && (
              <LogicBuilderStep2
                rules={finalLogicData.rules}
                serviceRows={serviceRows}
                setServiceRows={setServiceRows}
                savedLabPanels={savedLabPanels}
              />
            )}
            {currentStep === 3 && (
              <LogicBuilderStep3
                serviceRows={serviceRows}
                services={finalLogicData.services}
                onServicesDataChange={updateFinalServices}
                labPanels={finalLogicData.labPanels}
                savedLabPanels={savedLabPanels}
                onLabPanelsDataChange={updateFinalLabPanels}
              />
            )}
          </SegmentedStepper>
        )}
      </div>
    </Modal>
  );
};

export default NewLogicModal;

const styles = {
  modalContainer: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "1113px",
    height: "90vh", // Allow modal to use most of the screen height
    borderRadius: "8px",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  modalHeaderTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
    cursor: "pointer",
  },
  stepContent: {
    padding: "20px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
